import React from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { BrowserRouter, Redirect } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { matchRoutes, renderRoutes } from "react-router-config";
import { loadableReady } from "@loadable/component";

import createStore from "../shared/createStore";
import Routes from "../shared/routes";
import { canonicalizePath } from "../shared/utils";
import { selectLocalizationData } from "./features/localization/localizationSlice";
import { SUPPORTED_REGIONS } from "../shared/enums";
import RegionalPriceSet from "./RegionalPriceSet.jsx";

const state = window.__PRELOADED_STATE__;
delete window.__PRELOADED_STATE__;

const store = createStore(state);

const redirectRegionsToUS = [
  "/us",
  "/us/",
  "/ca",
  "/ca/",
  "/eu",
  "/eu/",
  "/uk",
  "/uk/",
  "/intl",
  "/intl/",
];

const redirectRegionsToCA = [
  "/",
  "/us",
  "/us/",
  "/eu",
  "/eu/",
  "/uk",
  "/uk/",
  "/intl",
  "/intl/",
];
const redirectRegionsToEU = ["/", "/us", "/us/", "/ca", "/ca/"];
const redirectRegionsToUK = [
  "/en-uk",
  "/en-uk/",
  "/",
  "/us",
  "/us/",
  "/ca",
  "/ca/",
  "/eu",
  "/eu/",
  "/en-gb",
  "/en-gb/",
  "/intl",
  "/intl/",
];

const AppRoutes = () => {
  const { region } = useSelector(selectLocalizationData);
  const regional = region || SUPPORTED_REGIONS.US;
  const location = useLocation();
  const search = location.search;

  const matched = matchRoutes(Routes, location.pathname);
  const routeMeta = matched.at(-1)?.route?.meta;
  const canonicalPath = canonicalizePath(location.pathname, routeMeta);
  if (canonicalPath !== location.pathname) {
    return <Redirect to={`${canonicalPath}${search || ""}`} />;
  }

  // the current localization region is "US" and the current location.pathname is one of the entries in redirectRegionsToUS
  if (regional === "US" && redirectRegionsToUS.includes(location.pathname)) {
    return <Redirect to={'/' + (search || '')} />;
  }

  // AU uses the same landing as US/CA (LandingAir at /)
  if (regional === "AU" && redirectRegionsToUS.includes(location.pathname)) {
    return <Redirect to={'/' + (search || '')} />;
  }

  // the current localization region is "CA" and the current location.pathname is one of the entries in redirectRegionsToCA
  if (regional === "CA" && redirectRegionsToCA.includes(location.pathname)) {
    return <Redirect to={'/ca' + (search || '')} />;
  }

  // the current localization region is neither "CA", "US", "GB", nor "AU" and the current location.pathname is one of the entries in redirectRegionsToEU
  if (!["CA", "US", "GB", "AU"].includes(regional) && redirectRegionsToEU.includes(location.pathname)) {
    return <Redirect to={'/intl' + (search || '')} />;
  }

  // the current localization region is "GB" and the current location.pathname is one of the entries in redirectRegionsToUK
  if (regional === "GB" && redirectRegionsToUK.includes(location.pathname)) {
    return <Redirect to={'/uk' + (search || '')} />;
  }

  return <>{renderRoutes(Routes)}</>;
};
loadableReady(() => {
  ReactDOM.hydrate(
    <Provider store={store}>
      <HelmetProvider>
        <BrowserRouter>
        <RegionalPriceSet />
          <AppRoutes />
        </BrowserRouter>
      </HelmetProvider>
    </Provider>,
    document.getElementById("root")
  );
});
